import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import useAsync from 'react-use/esm/useAsync';

export const PROJECT_SLUG_ANNOTATION = 'github.com/project-slug';

export type RegistryStatus = 'approved' | 'hold' | 'deprecated';

export type DependencyScope = 'runtime' | 'development';

const SCOPE_LABELS: Record<DependencyScope, string> = {
  runtime: 'Runtime',
  development: 'Development',
};

/** npm only; other ecosystems, and repositories whose package.json could not be read, show a dash. */
export const scopeLabel = (scope?: DependencyScope) =>
  scope ? SCOPE_LABELS[scope] : '—';

export type PackageUsage = {
  repo: string;
  owner: string | null;
  versions: string[];
  licenses: string[];
  scope?: DependencyScope;
  /** Null when the dependency was already present at the repository's first scan. */
  firstSeen: string | null;
  /** A third-party GitHub Action referenced by tag or branch rather than commit SHA. */
  unpinned: boolean;
  /** Every licence this repository has for the package is strong copyleft. */
  copyleft: boolean;
};

export type PackageSummary = {
  ecosystem: string;
  name: string;
  /** The registry entry that matched, such as `@aws-sdk/*`. */
  registryName?: string;
  status?: RegistryStatus;
  capability?: string;
  notes?: string;
  licenses: string[];
  copyleft: boolean;
  /** Runtime if any repository uses it at runtime. */
  scope?: DependencyScope;
  usages: PackageUsage[];
};

export type RepoScan = {
  repo: string;
  owner: string | null;
  scannedAt: string;
  error: string | null;
};

export type PackagesResponse = {
  registryError?: string;
  repos: RepoScan[];
  packages: PackageSummary[];
};

/** Packages in use, across every scanned repository or just `repo`. */
export function usePackages(repo?: string) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  return useAsync(async (): Promise<PackagesResponse> => {
    const baseUrl = await discoveryApi.getBaseUrl('package-inventory');
    const query = repo ? `?repo=${encodeURIComponent(repo)}` : '';
    const response = await fetchApi.fetch(`${baseUrl}/packages${query}`);
    if (!response.ok) {
      throw new Error(
        `Package inventory request failed: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  }, [discoveryApi, fetchApi, repo]);
}

export const isPackageInventoryAvailable = (entity: Entity) =>
  entity.kind === 'Component' &&
  Boolean(entity.metadata.annotations?.[PROJECT_SLUG_ANNOTATION]);

export const formatDate = (iso: string) => new Date(iso).toLocaleDateString();
